export type TimeRemaining = {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
  timeDifference: number;
};

export function calculateTimeDifference(targetDate: Date): TimeRemaining {
  const currentTime = new Date().getTime();
  const targetTime = targetDate.getTime();
  const timeDifference = targetTime - currentTime;

  if (timeDifference <= 0) {
    return {
      days: "00",
      hours: "00",
      minutes: "00",
      seconds: "00",
      timeDifference: 0,
    };
  }

  const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
  const hours = Math.floor((timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

  return {
    days: days.toString().padStart(2, "0"),
    hours: hours.toString().padStart(2, "0"),
    minutes: minutes.toString().padStart(2, "0"),
    seconds: seconds.toString().padStart(2, "0"),
    timeDifference,
  };
} 

/**
 * Formats countdown time, skipping leading zeros but keeping trailing zeros
 * @param days - Number of days
 * @param hours - Number of hours
 * @param minutes - Number of minutes
 * @param seconds - Number of seconds
 * @param labels - Custom labels for time units (default: { days: 'day', hours: 'hour', minutes: 'min', seconds: 'sec' })
 * @returns Formatted string like "1 hour 0 mins 0 secs" (skips leading zeros)
 */
export function formatCountdown(
    days: number,
    hours: number,
    minutes: number,
    seconds: number,
    labels = {
        days: 'day',
        hours: 'hour',
        minutes: 'min',
        seconds: 'sec',
    },
): string {
    const parts = [];
    let shouldInclude = false;

    // Helper function to handle singular/plural
    const formatUnit = (value: number, unit: string) => {
        return `${value} ${unit}${value !== 1 ? 's' : ''}`;
    };

    if (days > 0 || shouldInclude) {
        shouldInclude = true;
        parts.push(formatUnit(days, labels.days));
    }
    if (hours > 0 || shouldInclude) {
        shouldInclude = true;
        parts.push(formatUnit(hours, labels.hours));
    }
    if (minutes > 0 || shouldInclude) {
        shouldInclude = true;
        parts.push(formatUnit(minutes, labels.minutes));
    }
    if (seconds > 0 || shouldInclude) {
        shouldInclude = true;
        parts.push(formatUnit(seconds, labels.seconds));
    }

    // If everything is 0, return "0 secs" (using custom label if provided)
    if (parts.length === 0) {
        return `0 ${labels.seconds}s`;
    }

    return parts.join(' ');
}